// src/Components/Modules/UserSetting/AddEmployee.js
import React, { useState, useContext, useEffect } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import {
  Box, Typography, Paper, Grid, TextField, Button, IconButton, Tooltip,
  Stack, Avatar, Divider, MenuItem, Select, FormControl, InputLabel,
  InputAdornment, Tabs, Tab, Alert, Chip, CircularProgress, Card, CardContent
} from "@mui/material";
import {
  FaUserTie, FaEnvelope, FaPhone, FaBriefcase, FaMapMarkerAlt,
  FaIdCard, FaCalendarAlt, FaDollarSign, FaClipboardList, FaInfoCircle,
  FaBuilding, FaTint, FaUsers, FaHeartbeat, FaUniversity, FaFileUpload,
  FaGraduationCap, FaMedal, FaClock, FaFileAlt, FaUserFriends,
  FaMoneyBillWave, FaCalendarCheck, FaArrowLeft, FaSave, FaTimes,
  FaCamera, FaFilePdf, FaPlus, FaTrash,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
  position: "relative",
  overflow: "hidden",
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: "white",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  marginBottom: theme.spacing(3),
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  minHeight: 56,
  fontSize: "0.875rem",
  "&.Mui-selected": {
    color: "#667eea",
  },
}));

const AddEmployee = () => {
  const navigate = useNavigate();
  const { addEmployeeToList } = useContext(MotherContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  const [employmentType, setEmploymentType] = useState('permanent');
  const [employmentRule, setEmploymentRule] = useState(null);
  const [loadingRule, setLoadingRule] = useState(false);
  const [workstations, setWorkstations] = useState([]);

  // Salary Rules State
  const [companyRules, setCompanyRules] = useState({
    basic: 50,
    rent: 40,
    medical: 10,
    conv: 5,
    food_allowance_percent: 0,
    transport_allowance_percent: 0,
    mobile_allowance_percent: 0,
    overtime_rate: 2,
    working_days_per_month: 26,
    daily_working_hours: 8,
  });
  const [breakdown, setBreakdown] = useState({
    houseRent: 0,
    medical: 0,
    conveyance: 0,
    foodAllowance: 0,
    transportAllowance: 0,
    mobileAllowance: 0,
    gross: 0
  });

  // ✅ Cleaned up: Only keeping the necessary temporary states for adding new items
  const [newSkill, setNewSkill] = useState("");
  const [newEducation, setNewEducation] = useState({ degree: "", institution: "", year: "" });
  const [newExperience, setNewExperience] = useState({ company: "", position: "", duration: "", description: "" });

  // Form State (Contains all fields including arrays)
  const [formData, setFormData] = useState({
    employee_name: "",
    employee_email: "",
    employee_phone: "",
    nid: "",
    nid_passport_number: "",
    blood_group: "",
    employee_photo: null,
    address: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relation: "",
    department: "",
    employee_designation: "",
    employment_type: "permanent",
    joining_date: "",
    contract_start: "",
    contract_end: "",
    shift_type: "day",
    basic_salary: "",
    gross_salary: 0,
    house_rent: 0,
    medical_allowance: 0,
    conveyance: 0,
    food_allowance: 0,
    transport_allowance: 0,
    mobile_allowance: 0,
    bank_name: "",
    bank_account_number: "",
    bank_routing_number: "",
    tin_number: "",
    cv_file: null,
    skills: [],
    education: [],
    experience: [],
  });

  const employmentTypes = [
    { label: "Permanent", value: "permanent" },
    { label: "Probation", value: "probation" },
    { label: "Contractual", value: "contractual" },
    { label: "Part-Time", value: "part_time" },
    { label: "Intern", value: "intern" },
    { label: "Temporary", value: "temporary" },
    { label: "Freelance", value: "freelance" },
  ];

  const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const shiftTypes = [
    { label: "Day Shift", value: "day" },
    { label: "Night Shift", value: "night" },
    { label: "Rotational", value: "rotational" },
    { label: "Flexible", value: "flexible" },
  ];

  const emergencyRelations = ["Father", "Mother", "Spouse", "Sibling", "Child", "Friend", "Other"];

  useEffect(() => {
    AxiosAuthInstance.get("company/profile").then(res => {
      const rules = res.data.company.salary_rules || {};
      setCompanyRules({
        basic: rules.basic_percent ?? 50,
        rent: rules.house_rent_percent ?? 40,
        medical: rules.medical_percent ?? 10,
        conv: rules.conveyance_percent ?? 5,
        food_allowance_percent: rules.food_allowance_percent ?? 0,
        transport_allowance_percent: rules.transport_allowance_percent ?? 0,
        mobile_allowance_percent: rules.mobile_allowance_percent ?? 0,
        overtime_rate: rules.overtime_rate ?? 2,
        working_days_per_month: rules.working_days_per_month ?? 26,
        daily_working_hours: rules.daily_working_hours ?? 8,
      });
    }).catch(() => { });
  }, []);

  useEffect(() => {
    AxiosAuthInstance.get("workstations").then(res => setWorkstations(res.data)).catch(() => { });
  }, []);

  useEffect(() => {
    const fetchRule = async () => {
      setLoadingRule(true);
      try {
        const res = await AxiosAuthInstance.get(`company/employment-rules/type/${employmentType}`);
        if (res.data.status && res.data.rule) {
          setEmploymentRule(res.data.rule);
          if (res.data.rule.salary_rules && Object.keys(res.data.rule.salary_rules).length > 0) {
            setCompanyRules(prev => ({
              ...prev,
              basic: res.data.rule.salary_rules.basic_percent ?? prev.basic,
              rent: res.data.rule.salary_rules.house_rent_percent ?? prev.rent,
              medical: res.data.rule.salary_rules.medical_percent ?? prev.medical,
              conv: res.data.rule.salary_rules.conveyance_percent ?? prev.conv,
              food_allowance_percent: res.data.rule.salary_rules.food_allowance_percent ?? prev.food_allowance_percent,
              transport_allowance_percent: res.data.rule.salary_rules.transport_allowance_percent ?? prev.transport_allowance_percent,
              mobile_allowance_percent: res.data.rule.salary_rules.mobile_allowance_percent ?? prev.mobile_allowance_percent,
              overtime_rate: res.data.rule.salary_rules.overtime_rate ?? prev.overtime_rate,
              working_days_per_month: res.data.rule.salary_rules.working_days_per_month ?? prev.working_days_per_month,
              daily_working_hours: res.data.rule.salary_rules.daily_working_hours ?? prev.daily_working_hours,
            }));
          }
        } else {
          setEmploymentRule(null);
        }
      } catch (err) {
        setEmploymentRule(null);
      } finally {
        setLoadingRule(false);
      }
    };
    fetchRule();
  }, [employmentType]);

  const handleBasicChange = (val) => {
    const basic = parseFloat(val) || 0;
    const rent = (basic * companyRules.rent) / 100;
    const med = (basic * companyRules.medical) / 100;
    const conv = (basic * companyRules.conv) / 100;
    const food = (basic * (companyRules.food_allowance_percent || 0)) / 100;
    const transport = (basic * (companyRules.transport_allowance_percent || 0)) / 100;
    const mobile = (basic * (companyRules.mobile_allowance_percent || 0)) / 100;
    const gross = basic + rent + med + conv + food + transport + mobile;

    setBreakdown({
      houseRent: rent,
      medical: med,
      conveyance: conv,
      foodAllowance: food,
      transportAllowance: transport,
      mobileAllowance: mobile,
      gross
    });
    setFormData(prev => ({
      ...prev,
      basic_salary: basic,
      gross_salary: gross,
      house_rent: rent,
      medical_allowance: med,
      conveyance: conv,
      food_allowance: food,
      transport_allowance: transport,
      mobile_allowance: mobile,
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleFileChange = (field, file) => {
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const addSkill = () => {
    if (newSkill.trim()) {
      setFormData(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill("");
    }
  };

  const removeSkill = (index) => {
    setFormData(prev => ({ ...prev, skills: prev.skills.filter((_, i) => i !== index) }));
  };

  const addEducation = () => {
    if (newEducation.degree && newEducation.institution) {
      setFormData(prev => ({ ...prev, education: [...prev.education, { ...newEducation }] }));
      setNewEducation({ degree: "", institution: "", year: "" });
    }
  };

  const removeEducation = (index) => {
    setFormData(prev => ({ ...prev, education: prev.education.filter((_, i) => i !== index) }));
  };

  const addExperience = () => {
    if (newExperience.company && newExperience.position) {
      setFormData(prev => ({ ...prev, experience: [...prev.experience, { ...newExperience }] }));
      setNewExperience({ company: "", position: "", duration: "", description: "" });
    }
  };

  const removeExperience = (index) => {
    setFormData(prev => ({ ...prev, experience: prev.experience.filter((_, i) => i !== index) }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.employee_name.trim()) newErrors.employee_name = "Name is required";
    if (!formData.employee_email.trim()) newErrors.employee_email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.employee_email)) newErrors.employee_email = "Invalid email";
    if (!formData.employee_phone.trim()) newErrors.employee_phone = "Phone is required";
    if (!formData.nid.trim()) newErrors.nid = "NID is required";
    if (!formData.employee_designation.trim()) newErrors.employee_designation = "Designation is required";
    if (!formData.joining_date) newErrors.joining_date = "Joining date is required";
    if (!formData.basic_salary || formData.basic_salary <= 0) newErrors.basic_salary = "Basic salary is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Swal.fire({ icon: "warning", title: "Validation Error", text: "Please fill all required fields" });
      return;
    }

    setLoading(true);
    const formDataToSend = new FormData();

    // 1. Append all regular scalar fields
    formDataToSend.append("employee_name", formData.employee_name);
    formDataToSend.append("employee_email", formData.employee_email);
    formDataToSend.append("employee_phone", formData.employee_phone);
    formDataToSend.append("nid", formData.nid);
    formDataToSend.append("nid_passport_number", formData.nid_passport_number || "");
    formDataToSend.append("blood_group", formData.blood_group || "");
    formDataToSend.append("address", formData.address);
    formDataToSend.append("emergency_contact_name", formData.emergency_contact_name || "");
    formDataToSend.append("emergency_contact_phone", formData.emergency_contact_phone || "");
    formDataToSend.append("emergency_contact_relation", formData.emergency_contact_relation || "");
    formDataToSend.append("department", formData.department || "");
    formDataToSend.append("employee_designation", formData.employee_designation);
    formDataToSend.append("employment_type", formData.employment_type);
    formDataToSend.append("joining_date", formData.joining_date);
    formDataToSend.append("contract_start", formData.contract_start || "");
    formDataToSend.append("contract_end", formData.contract_end || "");
    formDataToSend.append("shift_type", formData.shift_type);
    formDataToSend.append("basic_salary", formData.basic_salary);
    formDataToSend.append("gross_salary", formData.gross_salary);
    formDataToSend.append("house_rent", formData.house_rent);
    formDataToSend.append("medical_allowance", formData.medical_allowance);
    formDataToSend.append("conveyance", formData.conveyance);
    formDataToSend.append("food_allowance", formData.food_allowance || 0);
    formDataToSend.append("transport_allowance", formData.transport_allowance || 0);
    formDataToSend.append("mobile_allowance", formData.mobile_allowance || 0);
    formDataToSend.append("bank_name", formData.bank_name || "");
    formDataToSend.append("bank_account_number", formData.bank_account_number || "");
    formDataToSend.append("bank_routing_number", formData.bank_routing_number || "");
    formDataToSend.append("tin_number", formData.tin_number || "");
    formDataToSend.append("workstation_id", formData.workstation_id || "");

    // 2. ✅ Append arrays using bracket notation so Laravel parses them natively
    formData.skills.forEach((skill, index) => {
      formDataToSend.append(`skills[${index}]`, skill);
    });

    formData.education.forEach((edu, index) => {
      formDataToSend.append(`education[${index}][degree]`, edu.degree);
      formDataToSend.append(`education[${index}][institution]`, edu.institution);
      formDataToSend.append(`education[${index}][year]`, edu.year);
    });

    formData.experience.forEach((exp, index) => {
      formDataToSend.append(`experience[${index}][company]`, exp.company);
      formDataToSend.append(`experience[${index}][position]`, exp.position);
      formDataToSend.append(`experience[${index}][duration]`, exp.duration);
      formDataToSend.append(`experience[${index}][description]`, exp.description);
    });

    // 3. Append files if they exist
    if (formData.employee_photo) {
      formDataToSend.append("employee_photo", formData.employee_photo);
    }
    if (formData.cv_file) {
      formDataToSend.append("cv_file", formData.cv_file);
    }

    try {
      const res = await AxiosAuthInstance.post("add_employee", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data.status === true) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: res.data.message || "Employee added successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
        if (res.data.employee) {
          addEmployeeToList(res.data.employee);
        }
        navigate("/hr/employees");
      }
    } catch (err) {
      console.error("Error details:", err.response?.data);

      const serverErrors = err.response?.data?.errors || {};
      const formattedErrors = {};
      Object.keys(serverErrors).forEach(key => {
        formattedErrors[key] = serverErrors[key][0];
      });
      setErrors(formattedErrors);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to add employee",
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { label: "Personal", icon: FaUserTie },
    { label: "Contact", icon: FaPhone },
    { label: "Job Info", icon: FaBriefcase },
    { label: "Salary", icon: FaMoneyBillWave },
    { label: "Bank", icon: FaUniversity },
    { label: "Documents", icon: FaFileAlt },
    { label: "Skills", icon: FaMedal },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Add Employee" />

      <GradientPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaUserTie style={{ marginRight: 10, verticalAlign: "middle" }} />
              Add New Employee
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Fill in the employee details to add them to your organization
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<FaArrowLeft />}
            onClick={() => navigate("/hr/employees")}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            Back to List
          </Button>
        </Box>
      </GradientPaper>

      <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTabs-indicator": {
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              height: 3,
            },
          }}
        >
          {tabs.map((tab, index) => {
            const IconComponent = tab.icon;
            return (
              <StyledTab
                key={index}
                icon={<IconComponent style={{ marginRight: 6 }} />}
                iconPosition="start"
                label={tab.label}
              />
            );
          })}
        </Tabs>
      </Paper>

      <SectionCard>
        {/* Personal Information */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2", mr: 2 }}><FaUserTie /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Personal Information</Typography>
                <Typography variant="body2" color="text.secondary">Basic employee details</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    width: 150, height: 150, borderRadius: "50%", bgcolor: "#f5f5f5", mx: "auto",
                    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                    overflow: "hidden", border: "3px dashed #bdbdbd", transition: "all 0.3s ease",
                    "&:hover": { borderColor: "#667eea", bgcolor: "#f0f2ff" },
                  }}
                  onClick={() => document.getElementById("photo-upload").click()}
                >
                  {formData.employee_photo ? (
                    <img src={URL.createObjectURL(formData.employee_photo)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Box sx={{ textAlign: "center" }}>
                      <FaCamera size={32} color="#bdbdbd" />
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>Upload Photo</Typography>
                    </Box>
                  )}
                </Box>
                <input type="file" id="photo-upload" accept="image/*" style={{ display: "none" }} onChange={(e) => handleFileChange("employee_photo", e.target.files[0])} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>Max 2MB | JPG, PNG</Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Full Name *" value={formData.employee_name} onChange={(e) => handleInputChange("employee_name", e.target.value)} error={!!errors.employee_name} helperText={errors.employee_name} InputProps={{ startAdornment: <InputAdornment position="start"><FaUserTie style={{ color: "#757575" }} /></InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Email Address *" type="email" value={formData.employee_email} onChange={(e) => handleInputChange("employee_email", e.target.value)} error={!!errors.employee_email} helperText={errors.employee_email} InputProps={{ startAdornment: <InputAdornment position="start"><FaEnvelope style={{ color: "#757575" }} /></InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Phone Number *" value={formData.employee_phone} onChange={(e) => handleInputChange("employee_phone", e.target.value)} error={!!errors.employee_phone} helperText={errors.employee_phone} InputProps={{ startAdornment: <InputAdornment position="start"><FaPhone style={{ color: "#757575" }} /></InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="NID Number *" value={formData.nid} onChange={(e) => handleInputChange("nid", e.target.value)} error={!!errors.nid} helperText={errors.nid} InputProps={{ startAdornment: <InputAdornment position="start"><FaIdCard style={{ color: "#757575" }} /></InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Passport Number" value={formData.nid_passport_number} onChange={(e) => handleInputChange("nid_passport_number", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaIdCard style={{ color: "#757575" }} /></InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Blood Group</InputLabel>
                      <Select value={formData.blood_group} onChange={(e) => handleInputChange("blood_group", e.target.value)} label="Blood Group" startAdornment={<InputAdornment position="start"><FaTint style={{ color: "#f44336" }} /></InputAdornment>}>
                        {bloodGroups.map((bg) => (<MenuItem key={bg} value={bg}>{bg}</MenuItem>))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Contact & Emergency */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", mr: 2 }}><FaPhone /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Contact & Emergency Information</Typography>
                <Typography variant="body2" color="text.secondary">Address and emergency contacts</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField fullWidth label="Full Address *" multiline rows={3} value={formData.address} onChange={(e) => handleInputChange("address", e.target.value)} error={!!errors.address} helperText={errors.address} InputProps={{ startAdornment: <InputAdornment position="start"><FaMapMarkerAlt style={{ color: "#757575" }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" icon={<FaHeartbeat />}>Emergency contact information is important for workplace safety</Alert>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Emergency Contact Name" value={formData.emergency_contact_name} onChange={(e) => handleInputChange("emergency_contact_name", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaUserFriends style={{ color: "#757575" }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Emergency Contact Phone" value={formData.emergency_contact_phone} onChange={(e) => handleInputChange("emergency_contact_phone", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaPhone style={{ color: "#757575" }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Relation</InputLabel>
                  <Select value={formData.emergency_contact_relation} onChange={(e) => handleInputChange("emergency_contact_relation", e.target.value)} label="Relation">
                    {emergencyRelations.map((rel) => (<MenuItem key={rel} value={rel}>{rel}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Job Information */}
        {activeTab === 2 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#fff3e0", color: "#ef6c00", mr: 2 }}><FaBriefcase /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Job & Employment Information</Typography>
                <Typography variant="body2" color="text.secondary">Position and employment details</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Department" value={formData.department} onChange={(e) => handleInputChange("department", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaBuilding style={{ color: "#757575" }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Designation *" value={formData.employee_designation} onChange={(e) => handleInputChange("employee_designation", e.target.value)} error={!!errors.employee_designation} helperText={errors.employee_designation} InputProps={{ startAdornment: <InputAdornment position="start"><FaBriefcase style={{ color: "#757575" }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Employment Type *</InputLabel>
                  <Select value={employmentType} onChange={(e) => { setEmploymentType(e.target.value); handleInputChange("employment_type", e.target.value); }} label="Employment Type *" startAdornment={<InputAdornment position="start"><FaClipboardList style={{ color: "#757575" }} /></InputAdornment>}>
                    {employmentTypes.map((type) => (<MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>))}
                  </Select>
                </FormControl>
                {loadingRule && <Typography variant="caption" color="info" sx={{ mt: 0.5 }}>Loading employment rules...</Typography>}
                {employmentRule && !loadingRule && (
                  <Alert severity="success" sx={{ mt: 1 }}>✓ {employmentRule.probation_months || 0} months probation, {employmentRule.resignation_notice_days || 30} days notice</Alert>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Workstation / Office</InputLabel>
                  <Select
                    value={formData.workstation_id}
                    onChange={(e) => handleInputChange("workstation_id", e.target.value)}
                    label="Workstation / Office"
                    startAdornment={<InputAdornment position="start"><FaMapMarkerAlt style={{ color: "#757575" }} /></InputAdornment>}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {workstations.map(ws => (
                      <MenuItem key={ws.id} value={ws.id}>{ws.name} ({ws.code})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>


              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Shift Type</InputLabel>
                  <Select value={formData.shift_type} onChange={(e) => handleInputChange("shift_type", e.target.value)} label="Shift Type" startAdornment={<InputAdornment position="start"><FaClock style={{ color: "#757575" }} /></InputAdornment>}>
                    {shiftTypes.map((shift) => (<MenuItem key={shift.value} value={shift.value}>{shift.label}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Joining Date *" type="date" value={formData.joining_date} onChange={(e) => handleInputChange("joining_date", e.target.value)} error={!!errors.joining_date} helperText={errors.joining_date} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><FaCalendarAlt style={{ color: "#757575" }} /></InputAdornment> }} />
              </Grid>
              {(employmentType === "contractual" || employmentType === "temporary") && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Contract Start" type="date" value={formData.contract_start} onChange={(e) => handleInputChange("contract_start", e.target.value)} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Contract End" type="date" value={formData.contract_end} onChange={(e) => handleInputChange("contract_end", e.target.value)} InputLabelProps={{ shrink: true }} />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        )}

        {/* Salary */}
        {activeTab === 3 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", mr: 2 }}><FaMoneyBillWave /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Salary & Benefits</Typography>
                <Typography variant="body2" color="text.secondary">Compensation details</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Basic Salary *"
                  type="number"
                  value={formData.basic_salary}
                  onChange={(e) => handleBasicChange(e.target.value)}
                  error={!!errors.basic_salary}
                  helperText={errors.basic_salary || "Gross salary will be auto-calculated"}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><FaDollarSign style={{ color: "#757575" }} /></InputAdornment>
                  }}
                />
              </Grid>

              {/* Salary Breakdown Preview */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: "#f8f9ff", border: "1px solid #e3f2fd" }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      <FaClipboardList style={{ marginRight: 6 }} />
                      Salary Breakdown Preview
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {/* Basic */}
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">Basic</Typography>
                          <Typography variant="h6" fontWeight={700} color="#1976d2">
                            ৳{(formData.basic_salary || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* House Rent */}
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            House Rent ({companyRules.rent}%)
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#2196f3">
                            ৳{breakdown.houseRent.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Medical */}
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Medical ({companyRules.medical}%)
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#4caf50">
                            ৳{breakdown.medical.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Conveyance */}
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Conveyance ({companyRules.conv}%)
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#ff9800">
                            ৳{breakdown.conveyance.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Food Allowance */}
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Food Allowance ({companyRules.food_allowance_percent}%)
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#9c27b0">
                            ৳{breakdown.foodAllowance.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Transport Allowance */}
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Transport ({companyRules.transport_allowance_percent}%)
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#00bcd4">
                            ৳{breakdown.transportAllowance.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Mobile Allowance */}
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Mobile ({companyRules.mobile_allowance_percent}%)
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#795548">
                            {breakdown.mobileAllowance.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Overtime Rate */}
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Overtime Rate
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#607d8b">
                            {companyRules.overtime_rate || 2}x
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            per hour
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Working Days & Hours - FIXED */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Working Days/Month
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#1976d2">
                            {companyRules.working_days_per_month || 26} days
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Daily Hours
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="#1976d2">
                            {companyRules.daily_working_hours || 8} hrs
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Gross Salary Total */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: "#667eea", borderRadius: 2, textAlign: "center" }}>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)" }}>
                        Gross Salary (Base Components)
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="white">
                        ৳{breakdown.gross.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", display: "block", mt: 0.5 }}>
                        + Additional allowances will be calculated separately
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Bank Details */}
        {activeTab === 4 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#f3e5f5", color: "#7b1fa2", mr: 2 }}><FaUniversity /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Bank & Financial Details</Typography>
                <Typography variant="body2" color="text.secondary">Bank account and tax information</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Bank Name" value={formData.bank_name} onChange={(e) => handleInputChange("bank_name", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaUniversity style={{ color: "#757575" }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Account Number" value={formData.bank_account_number} onChange={(e) => handleInputChange("bank_account_number", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaIdCard style={{ color: "#757575" }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Routing Number" value={formData.bank_routing_number} onChange={(e) => handleInputChange("bank_routing_number", e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="TIN Number" value={formData.tin_number} onChange={(e) => handleInputChange("tin_number", e.target.value)} helperText="Tax Identification Number" />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Documents */}
        {activeTab === 5 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#fce4ec", color: "#c2185b", mr: 2 }}><FaFileAlt /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Documents</Typography>
                <Typography variant="body2" color="text.secondary">Upload CV and other documents</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "2px dashed #bdbdbd", borderRadius: 2, p: 4, textAlign: "center", cursor: "pointer",
                    transition: "all 0.3s ease", "&:hover": { borderColor: "#667eea", bgcolor: "#f5f7ff" },
                  }}
                  onClick={() => document.getElementById("cv-upload").click()}
                >
                  <input type="file" id="cv-upload" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={(e) => handleFileChange("cv_file", e.target.files[0])} />
                  <FaFilePdf size={48} color="#bdbdbd" />
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 2 }}>
                    {formData.cv_file ? formData.cv_file.name : "Click to upload CV/Resume"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">PDF, DOC, DOCX (Max 5MB)</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Skills, Education, Experience */}
        {activeTab === 6 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#fff3e0", color: "#ef6c00", mr: 2 }}><FaMedal /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Skills, Education & Experience</Typography>
                <Typography variant="body2" color="text.secondary">Professional background</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Skills</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField size="small" placeholder="Add a skill..." value={newSkill} onChange={(e) => setNewSkill(e.target.value)} onKeyPress={(e) => e.key === "Enter" && addSkill()} sx={{ flex: 1 }} />
                <Button variant="contained" onClick={addSkill} startIcon={<FaPlus />}>Add</Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.skills.map((skill, index) => (
                  <Chip key={index} label={skill} onDelete={() => removeSkill(index)} color="primary" variant="outlined" />
                ))}
              </Stack>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom><FaGraduationCap style={{ marginRight: 6 }} /> Education</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <TextField size="small" fullWidth placeholder="Degree" value={newEducation.degree} onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField size="small" fullWidth placeholder="Institution" value={newEducation.institution} onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField size="small" fullWidth placeholder="Year" value={newEducation.year} onChange={(e) => setNewEducation({ ...newEducation, year: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button fullWidth variant="contained" onClick={addEducation} startIcon={<FaPlus />}>Add</Button>
                </Grid>
              </Grid>
              <Stack spacing={1}>
                {formData.education.map((edu, index) => (
                  <Paper key={index} sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{edu.degree}</Typography>
                      <Typography variant="body2" color="text.secondary">{edu.institution} {edu.year && `(${edu.year})`}</Typography>
                    </Box>
                    <IconButton color="error" onClick={() => removeEducation(index)}><FaTrash /></IconButton>
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom><FaBriefcase style={{ marginRight: 6 }} /> Work Experience</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                  <TextField size="small" fullWidth placeholder="Company" value={newExperience.company} onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField size="small" fullWidth placeholder="Position" value={newExperience.position} onChange={(e) => setNewExperience({ ...newExperience, position: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField size="small" fullWidth placeholder="Duration" value={newExperience.duration} onChange={(e) => setNewExperience({ ...newExperience, duration: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField size="small" fullWidth placeholder="Description" value={newExperience.description} onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button fullWidth variant="contained" onClick={addExperience} startIcon={<FaPlus />}>Add</Button>
                </Grid>
              </Grid>
              <Stack spacing={1}>
                {formData.experience.map((exp, index) => (
                  <Paper key={index} sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{exp.position}</Typography>
                      <Typography variant="body2" color="text.secondary">{exp.company} {exp.duration && `• ${exp.duration}`}</Typography>
                      {exp.description && <Typography variant="caption" color="text.secondary">{exp.description}</Typography>}
                    </Box>
                    <IconButton color="error" onClick={() => removeExperience(index)}><FaTrash /></IconButton>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </SectionCard>

      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate("/hr/employees")} startIcon={<FaTimes />} disabled={loading} sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2 }}>Cancel</Button>
        <Stack direction="row" spacing={2}>
          {activeTab > 0 && (
            <Button variant="outlined" onClick={() => setActiveTab(activeTab - 1)} sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2 }}>Previous</Button>
          )}
          {activeTab < tabs.length - 1 ? (
            <Button variant="contained" onClick={() => setActiveTab(activeTab + 1)} sx={{ textTransform: "none", fontWeight: 600, px: 4, borderRadius: 2, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>Next</Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FaSave />} sx={{ textTransform: "none", fontWeight: 600, px: 4, borderRadius: 2, background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)", "&:hover": { background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)" } }}>
              {loading ? "Adding..." : "Add Employee"}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default AddEmployee;